import React, { useState, useEffect, useCallback } from "react";
import {
  Globe,
  Code,
  Hash,
  MessageSquare,
  Mail,
  Phone,
  Send,
  Copy,
  Check,
  Settings,
  ToggleLeft,
  ToggleRight,
  ExternalLink,
  RefreshCw,
  ChevronDown,
  Shield,
  Zap,
  TestTube,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API_BASE_URL } from "../../constants";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const DEFAULT_CHANNELS_CONFIG = {
  widget: {
    enabled: true,
    position: "bottom-right",
    title: "Ask Lidy",
    brand_color: "#cc1111",
  },
  api: { enabled: false },
  slack: { enabled: false, webhook_url: "", channel: "" },
  sms: { enabled: false, twilio_sid: "", twilio_token: "", phone: "" },
  email: { enabled: false, address: "" },
};

const StatusBadge = ({ active, label }) => {
  const bg = active
    ? "bg-green-100 text-green-800"
    : "bg-slate-100 text-slate-500";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${active ? "bg-green-500" : "bg-slate-400"}`}
      />
      {label}
    </span>
  );
};

const ToggleSwitch = ({ enabled, onToggle, label }) => {
  const Icon = enabled ? ToggleRight : ToggleLeft;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center space-x-2 group"
      title={`${enabled ? "Disable" : "Enable"} ${label}`}
    >
      <Icon
        className={`w-7 h-7 transition-colors ${
          enabled
            ? "text-green-500 group-hover:text-green-600"
            : "text-slate-300 group-hover:text-slate-400"
        }`}
      />
      <span className="text-xs text-slate-500">
        {enabled ? "Enabled" : "Disabled"}
      </span>
    </button>
  );
};

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5 mr-1.5 text-green-500" />
          Copied
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5 mr-1.5" />
          Copy
        </>
      )}
    </button>
  );
};

const ChannelCard = ({
  icon: Icon,
  iconColor,
  title,
  description,
  badge,
  toggle,
  children,
}) => (
  <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
    <div className="flex items-start justify-between p-5 border-b border-slate-100">
      <div className="flex items-start space-x-3">
        <div className={`p-2 rounded-lg ${iconColor}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="flex items-center space-x-2.5">
            <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
            {badge}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>
      <div className="flex-shrink-0">{toggle}</div>
    </div>
    {children && <div className="p-5 space-y-4">{children}</div>}
  </div>
);

const InputField = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  disabled = false,
}) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1">
      {label}
    </label>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1">
      {label}
    </label>
    <div className="relative">
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  </div>
);

const InfoNote = ({ children }) => (
  <div className="flex items-start space-x-2 p-3 bg-slate-50 rounded-md border border-slate-100">
    <Zap className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
    <p className="text-xs text-slate-600 leading-relaxed">{children}</p>
  </div>
);

const ChannelManagement = () => {
  const { token } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);
  const [message, setMessage] = useState(null);
  const [config, setConfig] = useState({ ...DEFAULT_CHANNELS_CONFIG });

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const selectedFacility = facilities.find((f) => f.facility_id === facilityId);

  // Load facilities
  useEffect(() => {
    const fetchFacilities = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          setFacilities(list);
          if (list.length > 0 && !facilityId) {
            setFacilityId(list[0].facility_id || "");
          }
        }
      } catch (err) {
        console.error("Failed to load facilities:", err);
      }
      setLoading(false);
    };
    fetchFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Load channels_config when facility changes
  useEffect(() => {
    if (!facilityId || facilities.length === 0) return;
    const fac = facilities.find((f) => f.facility_id === facilityId);
    if (!fac) return;

    const saved = fac.channels_config || {};
    setConfig({
      widget: {
        ...DEFAULT_CHANNELS_CONFIG.widget,
        brand_color: fac.brand_color || "#cc1111",
        ...saved.widget,
      },
      api: { ...DEFAULT_CHANNELS_CONFIG.api, ...saved.api },
      slack: { ...DEFAULT_CHANNELS_CONFIG.slack, ...saved.slack },
      sms: { ...DEFAULT_CHANNELS_CONFIG.sms, ...saved.sms },
      email: { ...DEFAULT_CHANNELS_CONFIG.email, ...saved.email },
    });
    setMessage(null);
  }, [facilityId, facilities]);

  const updateChannel = useCallback((channel, field, value) => {
    setConfig((prev) => ({
      ...prev,
      [channel]: { ...prev[channel], [field]: value },
    }));
  }, []);

  const toggleChannel = useCallback((channel) => {
    setConfig((prev) => ({
      ...prev,
      [channel]: { ...prev[channel], enabled: !prev[channel].enabled },
    }));
  }, []);

  const handleSave = async () => {
    if (!facilityId) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${AUTH_URL}/facilities`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          facility_id: facilityId,
          channels_config: config,
          api_access_enabled: config.api.enabled,
        }),
      });
      if (res.ok) {
        setMessage({
          type: "success",
          text: "Channel configuration saved successfully.",
        });
        // Refresh facility data
        const refreshRes = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          setFacilities(Array.isArray(data) ? data : []);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage({
          type: "error",
          text: err.error || "Failed to save configuration.",
        });
      }
    } catch (err) {
      console.error("Save error:", err);
      setMessage({
        type: "error",
        text: "Network error. Could not save configuration.",
      });
    }
    setSaving(false);
  };

  const handleTestSlack = async () => {
    if (!config.slack.webhook_url) {
      setMessage({
        type: "error",
        text: "Enter a Slack webhook URL before testing.",
      });
      return;
    }
    setTestingSlack(true);
    setMessage(null);
    try {
      const res = await fetch(config.slack.webhook_url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: `[Test] Knowledge base channel connected for facility: ${selectedFacility?.display_name || facilityId}`,
        }),
      });
      if (res.ok) {
        setMessage({
          type: "success",
          text: "Slack test message sent successfully.",
        });
      } else {
        setMessage({
          type: "error",
          text: "Slack test failed. Check your webhook URL.",
        });
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: "Could not reach Slack webhook. Check the URL and try again.",
      });
    }
    setTestingSlack(false);
  };

  const buildEmbedSnippet = () => {
    if (!facilityId) return "";
    const pos = config.widget.position || "bottom-right";
    const title = config.widget.title || "Ask Lidy";
    const color = config.widget.brand_color || "#cc1111";
    return `<script>
  window.LidyConfig = {
    facilityId: '${facilityId}',
    position: '${pos}',
    title: '${title}',
    brandColor: '${color}'
  };
</script>
<script src="https://app.hyveappliedintelligence.com/static/lidy/lidy-embed.js"></script>`;
  };

  if (loading && facilities.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-5 h-5 text-slate-400 animate-spin mr-2" />
        <span className="text-sm text-slate-500">Loading facilities...</span>
      </div>
    );
  }

  if (!loading && facilities.length === 0) {
    return (
      <div className="text-center py-20">
        <Settings className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">No facilities found.</p>
        <p className="text-xs text-slate-400 mt-1">
          Create a facility first to configure delivery channels.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">
            Channel Management
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Configure how the knowledge base is delivered across channels
          </p>
        </div>
      </div>

      {/* Facility Selector */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
        <label className="block text-xs font-medium text-slate-600 mb-1.5">
          Select Facility
        </label>
        <div className="relative max-w-md">
          <select
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
          >
            {facilities.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.display_name || f.facility_name || f.facility_id}
                {f.state ? ` (${f.state})` : ""}
              </option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {/* Channel Cards */}
      <div className="space-y-4">
        {/* Widget Channel */}
        <ChannelCard
          icon={Globe}
          iconColor="bg-blue-50 text-blue-600"
          title="Embeddable Widget"
          description="Chatbot widget embedded on any website via script tag"
          badge={
            <StatusBadge
              active={config.widget.enabled}
              label={config.widget.enabled ? "Active" : "Inactive"}
            />
          }
          toggle={
            <ToggleSwitch
              enabled={config.widget.enabled}
              onToggle={() => toggleChannel("widget")}
              label="Widget"
            />
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <InputField
              label="Brand Color"
              value={config.widget.brand_color}
              onChange={(v) => updateChannel("widget", "brand_color", v)}
              placeholder="#cc1111"
            />
            <SelectField
              label="Position"
              value={config.widget.position}
              onChange={(v) => updateChannel("widget", "position", v)}
              options={[
                { value: "bottom-right", label: "Bottom Right" },
                { value: "bottom-left", label: "Bottom Left" },
              ]}
            />
            <InputField
              label="Title Text"
              value={config.widget.title}
              onChange={(v) => updateChannel("widget", "title", v)}
              placeholder="Ask Lidy"
            />
          </div>

          {/* Color preview */}
          <div className="flex items-center space-x-2">
            <span className="text-xs text-slate-500">Preview:</span>
            <div
              className="w-6 h-6 rounded-full border border-slate-200"
              style={{
                backgroundColor: config.widget.brand_color || "#cc1111",
              }}
            />
            <code className="text-xs text-slate-500">
              {config.widget.brand_color}
            </code>
          </div>

          {/* Embed snippet */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Embed Snippet
            </label>
            <div className="relative">
              <pre className="bg-slate-900 text-slate-100 text-xs p-4 rounded-md overflow-x-auto leading-relaxed">
                <code>{buildEmbedSnippet()}</code>
              </pre>
              <div className="absolute top-2 right-2">
                <CopyButton value={buildEmbedSnippet()} />
              </div>
            </div>
          </div>
        </ChannelCard>

        {/* REST API Channel */}
        <ChannelCard
          icon={Code}
          iconColor="bg-purple-50 text-purple-600"
          title="REST API"
          description="Programmatic access to the knowledge base via API endpoints"
          badge={
            <StatusBadge
              active={config.api.enabled}
              label={config.api.enabled ? "Active" : "Inactive"}
            />
          }
          toggle={
            <ToggleSwitch
              enabled={config.api.enabled}
              onToggle={() => toggleChannel("api")}
              label="REST API"
            />
          }
        >
          {selectedFacility?.api_key ? (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                API Key
              </label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 text-xs bg-slate-100 px-3 py-2 rounded-md text-slate-600 font-mono truncate">
                  {selectedFacility.api_key.slice(0, 8)}
                  {"********"}
                </code>
                <button
                  onClick={() => {
                    /* Navigate to API Access page */
                  }}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  API Docs
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-xs text-slate-500">
              <Shield className="w-4 h-4 text-slate-400" />
              <span>
                API key will be generated when the facility is provisioned.
              </span>
            </div>
          )}

          <InfoNote>
            Enable API access to allow external applications to query this
            facility's knowledge base. See the API Access page for full endpoint
            documentation and usage examples.
          </InfoNote>
        </ChannelCard>

        {/* Slack Channel */}
        <ChannelCard
          icon={Hash}
          iconColor="bg-emerald-50 text-emerald-600"
          title="Slack"
          description="Deliver KB answers directly to Slack channels via webhook"
          badge={
            <StatusBadge
              active={config.slack.enabled && !!config.slack.webhook_url}
              label={
                config.slack.enabled && config.slack.webhook_url
                  ? "Configured"
                  : "Not Configured"
              }
            />
          }
          toggle={
            <ToggleSwitch
              enabled={config.slack.enabled}
              onToggle={() => toggleChannel("slack")}
              label="Slack"
            />
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="Slack Webhook URL"
              value={config.slack.webhook_url}
              onChange={(v) => updateChannel("slack", "webhook_url", v)}
              placeholder="https://hooks.slack.com/services/..."
              disabled={!config.slack.enabled}
            />
            <InputField
              label="Channel Name"
              value={config.slack.channel}
              onChange={(v) => updateChannel("slack", "channel", v)}
              placeholder="#general"
              disabled={!config.slack.enabled}
            />
          </div>

          <div className="flex items-center">
            <button
              onClick={handleTestSlack}
              disabled={
                !config.slack.enabled ||
                !config.slack.webhook_url ||
                testingSlack
              }
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {testingSlack ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  Send Test Message
                </>
              )}
            </button>
          </div>
        </ChannelCard>

        {/* SMS (Twilio) Channel */}
        <ChannelCard
          icon={Phone}
          iconColor="bg-amber-50 text-amber-600"
          title="SMS (Twilio)"
          description="Receive questions via SMS and respond with KB-powered answers"
          badge={
            <StatusBadge
              active={config.sms.enabled && !!config.sms.twilio_sid}
              label={
                config.sms.enabled && config.sms.twilio_sid
                  ? "Configured"
                  : "Not Configured"
              }
            />
          }
          toggle={
            <ToggleSwitch
              enabled={config.sms.enabled}
              onToggle={() => toggleChannel("sms")}
              label="SMS"
            />
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <InputField
              label="Twilio Account SID"
              value={config.sms.twilio_sid}
              onChange={(v) => updateChannel("sms", "twilio_sid", v)}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              disabled={!config.sms.enabled}
            />
            <InputField
              label="Twilio Auth Token"
              value={config.sms.twilio_token}
              onChange={(v) => updateChannel("sms", "twilio_token", v)}
              placeholder="********"
              type="password"
              disabled={!config.sms.enabled}
            />
            <InputField
              label="Phone Number"
              value={config.sms.phone}
              onChange={(v) => updateChannel("sms", "phone", v)}
              placeholder="+1234567890"
              disabled={!config.sms.enabled}
            />
          </div>

          <InfoNote>
            Inbound SMS to this number will query the knowledge base and reply
            automatically. Requires an active Twilio account with messaging
            capabilities.
          </InfoNote>
        </ChannelCard>

        {/* Email Channel */}
        <ChannelCard
          icon={Mail}
          iconColor="bg-rose-50 text-rose-600"
          title="Email"
          description="Accept questions via email and respond with KB-powered answers"
          badge={
            <StatusBadge
              active={config.email.enabled && !!config.email.address}
              label={
                config.email.enabled && config.email.address
                  ? "Configured"
                  : "Not Configured"
              }
            />
          }
          toggle={
            <ToggleSwitch
              enabled={config.email.enabled}
              onToggle={() => toggleChannel("email")}
              label="Email"
            />
          }
        >
          <InputField
            label="Inbound Email Address"
            value={config.email.address}
            onChange={(v) => updateChannel("email", "address", v)}
            placeholder={`ask@${facilityId ? facilityId.toLowerCase() : "facility"}.hyveappliedintelligence.com`}
            disabled={!config.email.enabled}
          />

          <InfoNote>
            Forward questions to this address to receive KB-powered replies. The
            system will parse the email body, query the knowledge base, and send
            a formatted response.
          </InfoNote>
        </ChannelCard>
      </div>

      {/* Save Bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {message && (
              <div
                className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium ${
                  message.type === "success"
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}
              >
                {message.type === "success" ? (
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                ) : (
                  <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                )}
                {message.text}
              </div>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !facilityId}
            className="inline-flex items-center px-5 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Settings className="w-4 h-4 mr-2" />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChannelManagement;
